import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Users, TrendingUp, DollarSign, Calendar, Link2, Copy, Check, Eye } from "lucide-react";
import Layout from "../../components/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import { vendedoresAPI } from "../../services/api";

const INSTITUCIONAL_URL = "https://nomoz.tech";

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
};

const formatCurrency = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

const AdminVendedorDetalhe = () => {
  const { darkMode } = useTheme();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (desde) params.desde = desde;
    if (ate) params.ate = ate;
    vendedoresAPI.stats(id, params)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erro ao carregar dados"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [id, desde, ate]);

  const copyLink = async (type) => {
    if (!data?.vendedor) return;
    const code = type === "short" ? data.vendedor.short_id : data.vendedor.codigo;
    const url = `${INSTITUCIONAL_URL}/?v=${encodeURIComponent(code)}`;
    await navigator.clipboard.writeText(url);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
    toast.success(`Link copiado: ?v=${code}`);
  };

  const card = darkMode ? "bg-zinc-800 border-zinc-700" : "bg-white border-gray-200";
  const subtle = darkMode ? "text-gray-400" : "text-gray-500";

  if (loading) return <Layout><div className="p-8 text-center">Carregando...</div></Layout>;
  if (!data) return <Layout><div className="p-8 text-center">Vendedor não encontrado.</div></Layout>;

  const { vendedor: v, total_leads, ativos, confirmados, primeiro_lead, ultimo_lead, form_leads, comissao, ultimos_leads, views } = data;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin/vendedores" className={`p-2 rounded hover:bg-black/10 ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
            <ArrowLeft size={20} />
          </Link>
          <div className="flex-1">
            <h1 className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{v.nome}</h1>
            <p className={`text-sm ${subtle}`}>{v.email || "Sem email"} · Desde {formatDate(v.create_at)}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => copyLink("short")} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-mono ${darkMode ? "border-zinc-600 text-gray-300 hover:border-zinc-400" : "border-gray-200 text-gray-600 hover:border-gray-400"}`}>
              {copied === "short" ? <Check size={12} className="text-green-500" /> : <Link2 size={12} />} {v.short_id}
            </button>
            {v.codigo && (
              <button onClick={() => copyLink("custom")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-orange-200 text-xs font-mono text-nomoz-orange hover:border-orange-400">
                {copied === "custom" ? <Check size={12} className="text-green-500" /> : <Copy size={12} />} {v.codigo}
              </button>
            )}
          </div>
        </div>

        {/* Filtro de período */}
        <div className={`flex items-center gap-3 mb-6 flex-wrap`}>
          <span className={`text-sm font-medium ${subtle}`}>Período:</span>
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className={`px-3 py-1.5 rounded-md border text-sm ${darkMode ? "bg-zinc-700 border-zinc-600 text-white" : "bg-white border-gray-200 text-gray-700"}`}
          />
          <span className={subtle}>até</span>
          <input
            type="date"
            value={ate}
            onChange={(e) => setAte(e.target.value)}
            className={`px-3 py-1.5 rounded-md border text-sm ${darkMode ? "bg-zinc-700 border-zinc-600 text-white" : "bg-white border-gray-200 text-gray-700"}`}
          />
          {(desde || ate) && (
            <button
              onClick={() => { setDesde(""); setAte(""); }}
              className="text-xs text-nomoz-orange hover:underline"
            >
              Limpar
            </button>
          )}
        </div>

        {/* Cards de métricas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className={`p-4 rounded-lg border ${card}`}>
            <div className="flex items-center gap-2 mb-1">
              <Eye size={16} className="text-indigo-500" />
              <span className={`text-xs uppercase tracking-wide ${subtle}`}>Views</span>
            </div>
            <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{views?.total || 0}</div>
            <div className={`text-xs ${subtle}`}>{views?.unique || 0} únicos (por IP)</div>
          </div>
          <div className={`p-4 rounded-lg border ${card}`}>
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} className="text-nomoz-orange" />
              <span className={`text-xs uppercase tracking-wide ${subtle}`}>Cadastros</span>
            </div>
            <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{total_leads}</div>
            <div className={`text-xs ${subtle}`}>{ativos} ativos · {confirmados} confirmados</div>
          </div>
          <div className={`p-4 rounded-lg border ${card}`}>
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={16} className="text-blue-500" />
              <span className={`text-xs uppercase tracking-wide ${subtle}`}>Leads (form)</span>
            </div>
            <div className={`text-2xl font-bold ${darkMode ? "text-white" : "text-gray-900"}`}>{form_leads?.total || 0}</div>
            <div className={`text-xs ${subtle}`}>{form_leads?.convertidos || 0} convertidos</div>
          </div>
          <div className={`p-4 rounded-lg border ${card}`}>
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className="text-green-500" />
              <span className={`text-xs uppercase tracking-wide ${subtle}`}>Comissão</span>
            </div>
            <div className={`text-2xl font-bold text-green-500`}>{formatCurrency(comissao?.total)}</div>
            <div className={`text-xs ${subtle}`}>{((comissao?.pct_ciclo1 || v.comissao_pct_ciclo1 || 0.12) * 100).toFixed(0)}% 1º ciclo · {((comissao?.pct_demais || v.comissao_pct_demais || 0.04) * 100).toFixed(0)}% demais</div>
          </div>
          <div className={`p-4 rounded-lg border ${card}`}>
            <div className="flex items-center gap-2 mb-1">
              <Calendar size={16} className="text-purple-500" />
              <span className={`text-xs uppercase tracking-wide ${subtle}`}>Período</span>
            </div>
            <div className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>{formatDate(primeiro_lead)}</div>
            <div className={`text-xs ${subtle}`}>até {formatDate(ultimo_lead)}</div>
          </div>
        </div>

        {/* Detalhes de comissão */}
        {comissao?.detalhes?.length > 0 && (
          <div className={`rounded-lg border mb-6 ${card}`}>
            <div className="px-4 py-3 border-b border-inherit">
              <h2 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Comissão por cliente</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={darkMode ? "bg-zinc-900" : "bg-gray-50"}>
                  <tr>
                    <th className={`px-4 py-2 text-left ${subtle}`}>#</th>
                    <th className={`px-4 py-2 text-left ${subtle}`}>Data</th>
                    <th className={`px-4 py-2 text-right ${subtle}`}>Plano</th>
                    <th className={`px-4 py-2 text-right ${subtle}`}>%</th>
                    <th className={`px-4 py-2 text-right ${subtle}`}>Comissão</th>
                  </tr>
                </thead>
                <tbody>
                  {comissao.detalhes.map((d, i) => (
                    <tr key={i} className={`border-t ${darkMode ? "border-zinc-700" : "border-gray-100"}`}>
                      <td className={`px-4 py-2 ${subtle}`}>{d.ciclo}</td>
                      <td className="px-4 py-2">{formatDate(d.data)}</td>
                      <td className="px-4 py-2 text-right">{formatCurrency(d.plano_valor)}</td>
                      <td className="px-4 py-2 text-right">{(d.percentual * 100).toFixed(0)}%</td>
                      <td className="px-4 py-2 text-right font-medium text-green-500">{formatCurrency(d.comissao)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t-2 ${darkMode ? "border-zinc-600" : "border-gray-300"}`}>
                    <td colSpan={4} className="px-4 py-2 font-semibold text-right">Total</td>
                    <td className="px-4 py-2 text-right font-bold text-green-500">{formatCurrency(comissao.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Últimos leads */}
        <div className={`rounded-lg border ${card}`}>
          <div className="px-4 py-3 border-b border-inherit">
            <h2 className={`font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>Últimos cadastros</h2>
          </div>
          {ultimos_leads?.length > 0 ? (
            <div className="divide-y divide-inherit">
              {ultimos_leads.map((l) => (
                <div key={l.id} className="px-4 py-3 flex items-center justify-between">
                  <div>
                    <div className={`font-medium text-sm ${darkMode ? "text-white" : "text-gray-900"}`}>{l.nome}</div>
                    <div className={`text-xs ${subtle}`}>{l.email}</div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs ${subtle}`}>{formatDate(l.create_at)}</div>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${
                      l.status === 1 && l.email_confirmado
                        ? "bg-green-500/20 text-green-500"
                        : l.status === 1
                          ? "bg-yellow-500/20 text-yellow-600"
                          : "bg-gray-500/20 text-gray-500"
                    }`}>
                      {l.status === 1 && l.email_confirmado ? "Ativo" : l.status === 1 ? "Pendente" : "Suspenso"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`p-6 text-center text-sm ${subtle}`}>Nenhum cadastro ainda.</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminVendedorDetalhe;
